import { useState, useCallback } from "react"
import { toast } from "react-toastify"
import { H1, P } from "~/ui-rtk/components/ui/typography"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import {
  useAnalyzeCreativesMutation,
  useExecuteCreativeCandidateMutation,
} from "~/ui-rtk/api/creativeManagementApi"
import AdsetTable from "./components/AdsetTable"
import ActionBar from "./components/ActionBar"
import ReviewModal from "./components/ReviewModal"
import IrrelevantInputsModal from "./components/IrrelevantInputsModal"
import { useCreativeMetrics } from "./hooks/useCreativeMetrics"
import type {
  AnalyzeCreativesResponse,
  CandidateDecisionInput,
  CreativePlaybook,
  ExecuteRowResult,
  IrrelevantInputs,
} from "./types"

export default function CreativeManagement() {
  const { rows, isLoading, dataThrough, refetch } = useCreativeMetrics()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activePlaybook, setActivePlaybook] = useState<CreativePlaybook | null>(
    null,
  )
  const [analyzeResult, setAnalyzeResult] =
    useState<AnalyzeCreativesResponse | null>(null)
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [isInputsOpen, setIsInputsOpen] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const [analyzeCreatives, { isLoading: isAnalyzing }] =
    useAnalyzeCreativesMutation()
  const [executeCandidate] = useExecuteCreativeCandidateMutation()

  const handleToggleSelect = useCallback((adsetId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(adsetId)) {
        next.delete(adsetId)
      } else {
        next.add(adsetId)
      }
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === rows.length) {
        return new Set()
      }
      return new Set(rows.map(r => r.adsetId))
    })
  }, [rows])

  const runAnalyze = useCallback(
    async (playbook: CreativePlaybook, inputs?: IrrelevantInputs) => {
      setActivePlaybook(playbook)
      try {
        const result = await analyzeCreatives({
          platform: "meta",
          data: {
            adsetIds: Array.from(selectedIds),
            playbook,
            ...(inputs ? { inputs } : {}),
          },
        }).unwrap()
        // Server-side analyze failures reject the promise (the run is marked
        // FAILED in the DB and the HTTP call throws), so the catch below is the
        // only failure path — a 200 always carries a READY result.
        setAnalyzeResult(result)
        setIsReviewOpen(true)
      } catch (err) {
        // Surface server messages (validation failures, similarity-cap 422s)
        // instead of swallowing them behind the generic toast.
        toast.error(
          getServerErrorMessage(err, "Analysis failed. Please try again."),
        )
        setActivePlaybook(null)
      }
    },
    [analyzeCreatives, selectedIds],
  )

  const handleRunPlaybook = useCallback(
    (playbook: CreativePlaybook) => {
      // IRRELEVANT needs the user's dead products / ended sales first
      if (playbook === "IRRELEVANT") {
        setIsInputsOpen(true)
        return
      }
      runAnalyze(playbook)
    },
    [runAnalyze],
  )

  const handleInputsSubmit = useCallback(
    (inputs: IrrelevantInputs) => {
      setIsInputsOpen(false)
      runAnalyze("IRRELEVANT", inputs)
    },
    [runAnalyze],
  )

  // Sequentially record every decision so per-ad failures surface
  // individually; a failed call never aborts the batch.
  const handleExecute = useCallback(
    async (
      decisions: CandidateDecisionInput[],
    ): Promise<ExecuteRowResult[]> => {
      setProgress({ done: 0, total: decisions.length })
      const results: ExecuteRowResult[] = []

      for (let i = 0; i < decisions.length; i++) {
        const decision = decisions[i]
        try {
          const response = await executeCandidate({
            candidateId: decision.candidateId,
            data: { decision: decision.decision },
          }).unwrap()
          results.push({
            candidateId: decision.candidateId,
            adName: decision.adName,
            adsetName: decision.adsetName,
            outcome: response.outcome,
            error: response.error,
          })
        } catch (err) {
          // unwrap() rejects with the RTK error object, not an Error — pull
          // the server message (e.g. the 422 blocked-candidate refusal) out.
          results.push({
            candidateId: decision.candidateId,
            adName: decision.adName,
            adsetName: decision.adsetName,
            outcome: "FAILED",
            error: getServerErrorMessage(err, "Request failed"),
          })
        }
        setProgress({ done: i + 1, total: decisions.length })
      }

      // Clear selection and refresh so archived ads drop out of the counts
      setSelectedIds(new Set())
      refetch()
      return results
    },
    [executeCandidate, refetch],
  )

  const handleReviewClose = useCallback(() => {
    setIsReviewOpen(false)
    setAnalyzeResult(null)
    setActivePlaybook(null)
    setProgress({ done: 0, total: 0 })
  }, [])

  const handleCancelSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  return (
    <div className="h-full px-system-md py-system-sm pb-24">
      <div className="mb-system-lg">
        <H1 className="text-white font-medium">Creative Management</H1>
        <P className="text-gray-400 text-sm">
          Select Meta ad sets and run archival playbooks to retire
          underperforming, irrelevant, or duplicative ads
        </P>
      </div>

      {isLoading ? (
        <div className="border border-basic-blue rounded-lg p-8">
          <div className="animate-pulse space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-basic-dark-blue rounded" />
            ))}
          </div>
        </div>
      ) : (
        <AdsetTable
          rows={rows}
          selectedIds={selectedIds}
          dataThrough={dataThrough}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
        />
      )}

      <ActionBar
        selectedCount={selectedIds.size}
        analyzingPlaybook={isAnalyzing ? activePlaybook : null}
        onRun={handleRunPlaybook}
        onCancel={handleCancelSelection}
      />

      <IrrelevantInputsModal
        isOpen={isInputsOpen}
        onClose={() => setIsInputsOpen(false)}
        onSubmit={handleInputsSubmit}
      />

      <ReviewModal
        isOpen={isReviewOpen}
        playbook={activePlaybook}
        result={analyzeResult}
        progress={progress}
        onClose={handleReviewClose}
        onExecute={handleExecute}
      />
    </div>
  )
}
